// React
import { useEffect, useState } from 'react';
import { HashRouter as Router, Route, Routes } from 'react-router-dom';
// Microsoft
import {
  ReactPlugin,
  AppInsightsContext,
} from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
// Utils
import { createBrowserHistory } from 'history';
// Pages
import Privacy from './Privacy';
import TermsOfUse from './TermsOfUse';
import { MyHub } from './pages/MyHub';
import { MyApps } from './pages/MyApps';
import { Settings } from './pages/Settings';
import Config from './Config';
// Styling
import 'react-toastify/dist/ReactToastify.css';
import { DefaultTheme, ThemeProvider } from 'styled-components';
import {
  initializeIcons,
  ThemeProvider as FluentThemeProvider,
} from '@fluentui/react';
import { GlobalStyles } from '../styles/global';
import { AppContexts } from './contexts';
import { TtsTutorial } from './widgets/TutorialsExperience/TtsTutorial';
import { FluentProvider, Spinner } from '@fluentui/react-components';
import * as microsoftTeams from '@microsoft/teams-js';
import { AppTheme, getTheme } from '../styles/themes';
import { Provider } from '@fluentui/react-northstar';
import { useTeamsUserCredential } from '@microsoft/teamsfx-react';
import { TeamsFxContext } from './Context';
import { ToastContainer, toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { TtsApp } from './pages/TtsApp';

export const App = () => {
  //const { loading } = useTeamsFx();
  const { loading, theme, themeString, teamsUserCredential } =
    useTeamsUserCredential({
      initiateLoginEndpoint:
        process.env.REACT_APP_NGROK === 'true'
          ? process.env.REACT_APP_START_LOGIN_PAGE_URL_NGROK!
          : process.env.REACT_APP_START_LOGIN_PAGE_URL!,
      clientId:
        process.env.REACT_APP_NGROK === 'true'
          ? process.env.REACT_APP_CLIENT_ID_NGROK!
          : process.env.REACT_APP_CLIENT_ID!,
    });

  //initiateLoginEndpoint: process.env.REACT_APP_NGROK ? process.env.REACT_APP_START_LOGIN_PAGE_URL_NGROK! : process.env.REACT_APP_START_LOGIN_PAGE_URL!,
  //clientId: process.env.REACT_APP_NGROK ? process.env.REACT_APP_CLIENT_ID_NGROK! : process.env.REACT_APP_CLIENT_ID!,

  const [context, setContext] = useState<microsoftTeams.app.Context>();

  useEffect(() => {
    loading &&
      microsoftTeams.app.initialize().catch((error) => {
        toast(
          'Error initializing the app, maybe you are not in a supported plataform.',
          { type: 'error' }
        );
      });
  }, [loading]);

  const [activeTheme, setActiveTheme] = useState<AppTheme>(getTheme('default'));
  const [locale, setLocale] = useState('en-us');
  const [hostName, setHost] = useState<string>('');

  const { i18n } = useTranslation();

  useEffect(() => {
    microsoftTeams.app
      .getContext()
      .then((context) => {
        setContext(context);
        setHost(context.app.host.name);

        const currentLocale = context.app.locale;

        if (currentLocale !== locale) {
          setLocale(currentLocale);
          i18n.changeLanguage(currentLocale);
        }

        const themeName = context.app.theme || 'default';
        const theme = getTheme(themeName);
        if (theme !== activeTheme) {
          setActiveTheme(theme);
        }
      })
      .catch(() => {
        toast.error(
          'Error getting the context, maybe you are not in a supported plataform.'
        );
      });

    microsoftTeams.app.registerOnThemeChangeHandler((themeName: string) => {
      const theme = getTheme(themeName);
      if (theme !== activeTheme) {
        setActiveTheme(theme);
      }
    });
  }, []);

  const browserHistory = createBrowserHistory();
  const reactPlugin = new ReactPlugin();

  const appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: process.env.REACT_APP_APP_INSIGHTS,
      enableAutoRouteTracking: true,
      extensionConfig: {
        [reactPlugin.identifier]: { history: browserHistory },
      },
    },
  });

  if (process.env.REACT_APP_APP_INSIGHTS !== '') appInsights.loadAppInsights();

  initializeIcons();
  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <Provider style={{ background: activeTheme.base.white }}>
        <TeamsFxContext.Provider
          value={{ theme, themeString, teamsUserCredential, hostName, locale }}
        >
          <ThemeProvider theme={activeTheme.base as DefaultTheme.DefaultTheme}>
            <FluentProvider theme={activeTheme.newFluent}>
              <FluentThemeProvider applyTo="body" theme={activeTheme.fluent}>
                <AppContexts>
                  <Router>
                    {loading ? (
                      <Spinner size="large" />
                    ) : (
                      <>
                        <Routes>
                          <Route path="/privacy" element={<Privacy />} />
                          <Route path="/termsofuse" element={<TermsOfUse />} />
                          <Route
                            path="/myhub"
                            element={<MyHub context={context} />}
                          />
                          <Route
                            path="/myApps/*"
                            element={<MyApps context={context} />}
                          />
                          <Route
                            path="/ttsapp/*"
                            element={<TtsApp context={context} />}
                          />
                          <Route
                            path="/settings/*"
                            element={<Settings context={context} />}
                          />
                          <Route path="/config" element={<Config />} />
                          <Route path="/test" element={<Config />} />
                        </Routes>
                      </>
                    )}
                  </Router>
                  <TtsTutorial />
                </AppContexts>
                <ToastContainer
                  theme={context?.app.theme === 'dark' ? 'dark' : 'light'}
                />
                <GlobalStyles />
              </FluentThemeProvider>
            </FluentProvider>
          </ThemeProvider>
        </TeamsFxContext.Provider>
      </Provider>
    </AppInsightsContext.Provider>
  );
};
